import { calculateTax } from './taxCalculator';

const DEFAULT_STANDARD_MONTHLY_HOURS = 173.33;
const DEFAULT_STANDARD_DAILY_HOURS = 8;

const TAX_MODE_BY_BRACKET = {
  standard: 'standard',
  reduced: 'reduced',
  high: 'standard',
  exempt: 'exempt'
};

const TAX_MULTIPLIER_BY_BRACKET = {
  standard: 1,
  reduced: 0.8,
  high: 1.25,
  exempt: 0
};

function daysBetweenInclusive(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.max(0, Math.floor((end - start) / msPerDay) + 1);
}

function roundCurrency(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function normalizePayrollRecord(payrollRecord = {}) {
  return {
    employeeId: payrollRecord.employeeId || '',
    salaryType: payrollRecord.salaryType || 'monthly',
    employmentType: payrollRecord.employmentType || 'fullTime',
    baseSalary: Number(payrollRecord.baseSalary || 0),
    hourlyRate: Number(payrollRecord.hourlyRate || payrollRecord.baseSalary || 0),
    standardHoursPerMonth: Number(payrollRecord.standardHoursPerMonth || DEFAULT_STANDARD_MONTHLY_HOURS),
    workDaysPerMonth: Number(payrollRecord.workDaysPerMonth || 21.67),
    taxBracket: payrollRecord.taxBracket || 'standard',
    customDeductions: Array.isArray(payrollRecord.customDeductions) ? payrollRecord.customDeductions : [],
    customBenefits: Array.isArray(payrollRecord.customBenefits) ? payrollRecord.customBenefits : [],
    payeEnabled: payrollRecord.payeEnabled !== false,
    uifEmployeeEnabled: payrollRecord.uifEmployeeEnabled !== false,
    uifEmployerEnabled: payrollRecord.uifEmployerEnabled !== false,
    sdlEnabled: payrollRecord.sdlEnabled === true,
    leaveAccrualEnabled: payrollRecord.leaveAccrualEnabled !== false,
    paymentMethod: payrollRecord.paymentMethod || 'bankTransfer',
    bankDetails: payrollRecord.bankDetails || {}
  };
}

function resolveHoursWorked(payrollRecord, payRun, timeEntries = []) {
  if (timeEntries.length > 0) {
    return roundCurrency(
      timeEntries.reduce((sum, entry) => sum + Number(entry.hoursWorked || entry.hours || 0), 0)
    );
  }

  const daysInPeriod = daysBetweenInclusive(payRun?.startDate, payRun?.endDate);
  if (!daysInPeriod) return 0;

  const hoursPerDay = Number(payrollRecord.hoursPerDay || DEFAULT_STANDARD_DAILY_HOURS);
  const estimatedWorkingDays = Math.min(daysInPeriod, Math.round((daysInPeriod * 5) / 7));

  return roundCurrency(estimatedWorkingDays * hoursPerDay);
}

function calculateGrossEarnings(payrollRecord, payRun, timeEntries = []) {
  if (payrollRecord.salaryType === 'hourly') {
    const hoursWorked = resolveHoursWorked(payrollRecord, payRun, timeEntries);
    return {
      grossAmount: roundCurrency(hoursWorked * payrollRecord.hourlyRate),
      earnings: [
        {
          type: 'base',
          description: 'Ordinary hours',
          quantity: hoursWorked,
          rate: payrollRecord.hourlyRate,
          amount: roundCurrency(hoursWorked * payrollRecord.hourlyRate)
        }
      ]
    };
  }

  if (payrollRecord.salaryType === 'annual') {
    const monthlyEquivalent = payrollRecord.baseSalary / 12;
    return {
      grossAmount: roundCurrency(monthlyEquivalent),
      earnings: [
        {
          type: 'base',
          description: 'Monthly salary equivalent',
          quantity: 1,
          rate: roundCurrency(monthlyEquivalent),
          amount: roundCurrency(monthlyEquivalent)
        }
      ]
    };
  }

  if (payrollRecord.employmentType === 'contractor' || payrollRecord.salaryType === 'contract') {
    return {
      grossAmount: roundCurrency(payrollRecord.baseSalary),
      earnings: [
        {
          type: 'contract',
          description: 'Contract or invoice amount',
          quantity: 1,
          rate: roundCurrency(payrollRecord.baseSalary),
          amount: roundCurrency(payrollRecord.baseSalary)
        }
      ]
    };
  }

  return {
    grossAmount: roundCurrency(payrollRecord.baseSalary),
    earnings: [
      {
        type: 'base',
        description: 'Basic salary',
        quantity: 1,
        rate: roundCurrency(payrollRecord.baseSalary),
        amount: roundCurrency(payrollRecord.baseSalary)
      }
    ]
  };
}

function calculateLineItems(items, grossAmount) {
  return items
    .map((item) => {
      const isPercentage = item.type === 'percentage';
      const amount = isPercentage
        ? roundCurrency((grossAmount * Number(item.value || 0)) / 100)
        : roundCurrency(Number(item.value || 0));

      return {
        name: item.name || 'Adjustment',
        amount,
        description: item.description || '',
        calculationType: isPercentage ? 'percentage' : 'fixed',
        calculationValue: Number(item.value || 0)
      };
    })
    .filter((item) => item.amount > 0);
}

function calculateStatutoryDeductions(payrollRecord, grossAmount, paymentDate) {
  const deductions = [];
  const employerContributions = [];
  const bracketMode = TAX_MODE_BY_BRACKET[payrollRecord.taxBracket] || 'standard';
  const taxMultiplier = TAX_MULTIPLIER_BY_BRACKET[payrollRecord.taxBracket] ?? 1;

  if (payrollRecord.payeEnabled && payrollRecord.employmentType !== 'contractor') {
    const baseTax = calculateTax(grossAmount, bracketMode, paymentDate || new Date());
    const payeAmount = roundCurrency(baseTax * taxMultiplier);
    if (payeAmount > 0) {
      deductions.push({
        name: 'PAYE',
        amount: payeAmount,
        description: 'Estimated employee tax withholding'
      });
    }
  }

  if (payrollRecord.uifEmployeeEnabled && payrollRecord.employmentType !== 'contractor') {
    deductions.push({
      name: 'UIF',
      amount: roundCurrency(grossAmount * 0.01),
      description: 'Estimated employee UIF contribution'
    });
  }

  if (payrollRecord.uifEmployerEnabled && payrollRecord.employmentType !== 'contractor') {
    employerContributions.push({
      name: 'Employer UIF',
      amount: roundCurrency(grossAmount * 0.01),
      description: 'Estimated employer UIF contribution'
    });
  }

  if (payrollRecord.sdlEnabled && payrollRecord.employmentType !== 'contractor') {
    employerContributions.push({
      name: 'SDL',
      amount: roundCurrency(grossAmount * 0.01),
      description: 'Estimated Skills Development Levy'
    });
  }

  return {
    statutoryDeductions: deductions,
    employerContributions
  };
}

export function buildPayslipRecord({
  payrollRecord,
  payRun,
  employee = {},
  businessId,
  timeEntries = [],
  existingYtd = {}
}) {
  const normalizedPayroll = normalizePayrollRecord(payrollRecord);
  const { grossAmount, earnings } = calculateGrossEarnings(normalizedPayroll, payRun, timeEntries);
  const customDeductions = calculateLineItems(normalizedPayroll.customDeductions, grossAmount);
  const benefits = calculateLineItems(normalizedPayroll.customBenefits, grossAmount);
  const { statutoryDeductions, employerContributions } = calculateStatutoryDeductions(
    normalizedPayroll,
    grossAmount,
    payRun?.paymentDate
  );

  const deductions = [...statutoryDeductions, ...customDeductions];
  const totalDeductions = roundCurrency(deductions.reduce((sum, item) => sum + item.amount, 0));
  const totalBenefits = roundCurrency(benefits.reduce((sum, item) => sum + item.amount, 0));
  const netAmount = roundCurrency(grossAmount - totalDeductions + totalBenefits);

  return {
    payRunId: payRun?.id || '',
    businessId: businessId || payRun?.businessId || '',
    employeeId: normalizedPayroll.employeeId,
    employeeName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
    employmentType: normalizedPayroll.employmentType,
    salaryType: normalizedPayroll.salaryType,
    paymentMethod: normalizedPayroll.paymentMethod,
    bankDetails: normalizedPayroll.bankDetails,
    payPeriod: {
      startDate: payRun?.startDate || null,
      endDate: payRun?.endDate || null
    },
    paymentDate: payRun?.paymentDate || null,
    earnings,
    deductions,
    benefits,
    employerContributions,
    grossAmount,
    netAmount,
    totalDeductions,
    totalBenefits,
    notes: payRun?.notes || '',
    ytdEarnings: roundCurrency(Number(existingYtd.ytdEarnings || 0) + grossAmount),
    ytdDeductions: roundCurrency(Number(existingYtd.ytdDeductions || 0) + totalDeductions),
    ytdTaxes: roundCurrency(
      Number(existingYtd.ytdTaxes || 0) +
        deductions
          .filter((item) => item.name === 'PAYE')
          .reduce((sum, item) => sum + item.amount, 0)
    )
  };
}

export function buildPayRunSummary(payrollRecords = [], payRun) {
  const previews = payrollRecords.map((record) =>
    buildPayslipRecord({
      payrollRecord: record,
      payRun,
      employee: record.employee || {},
      businessId: payRun?.businessId
    })
  );

  return {
    employeeCount: previews.length,
    totalAmount: roundCurrency(previews.reduce((sum, preview) => sum + preview.netAmount, 0)),
    totalGross: roundCurrency(previews.reduce((sum, preview) => sum + preview.grossAmount, 0)),
    totalDeductions: roundCurrency(previews.reduce((sum, preview) => sum + preview.totalDeductions, 0)),
    totalEmployerContributions: roundCurrency(
      previews.reduce(
        (sum, preview) =>
          sum + preview.employerContributions.reduce((inner, contribution) => inner + contribution.amount, 0),
        0
      )
    ),
    previews
  };
}

export function summarizePayslipStatus(payslips = [], employeeCount = 0) {
  const sentCount = payslips.filter((item) => item.status === 'sent').length;
  const draftCount = payslips.filter((item) => item.status === 'draft').length;
  const completion = employeeCount > 0 ? Math.round((payslips.length / employeeCount) * 100) : 0;

  return {
    payslipCount: payslips.length,
    sentCount,
    draftCount,
    completion
  };
}
